/**
 * How she looks today, as opposed to what she says.
 *
 * `mood.ts` answers "what has the diary been like lately" in one of six words
 * and hands that to the catalogue. This file answers the neighbouring
 * question — what colour is she, how fast does she breathe, how sharply does
 * a toast arrive — and it has to answer it for CSS, which cannot import
 * TypeScript. So the tone resolves to a single string that gets stamped on
 * the document element, and every persona surface reads its palette from the
 * custom properties that string selects.
 *
 * Two additions on top of the mood: Wednesday, which sharpens an otherwise
 * ordinary day into the „Aštri" register, and the handful of dates a year the
 * whole instrument is allowed to wear the day. Both are pure functions of a
 * date and facts that are already synced, for the same reason moods are: the
 * lock screen and the diary must never disagree about who she is today.
 */

import { isBirthday, uzgavenesDate } from './calendar.js'
import type { MoodKey } from './mood.js'

/**
 * The mood palette plus one register that is not a mood. `astri` is the
 * Wednesday sharpening — a way of speaking rather than a state of the diary,
 * which is exactly why it lives here and not in `MoodKey`.
 */
export type PresenceTone = MoodKey | 'astri'

/** Wednesday. The day the register sharpens, per PERSONA.md. */
const ASTRI_WEEKDAY = 3

/**
 * Wednesday only sharpens a day that has nothing else going on. A real mood
 * outranks it every time: a fortnight of silence, a genuinely bad week or a
 * birthday is not improved by being delivered more sharply, and „Aštri" on
 * top of `neglected` reads as kicking someone who is already not here.
 */
export function presenceTone(date: Date, mood: MoodKey): PresenceTone {
  if (mood !== 'plain') return mood
  return date.getDay() === ASTRI_WEEKDAY ? 'astri' : 'plain'
}

/**
 * The five days a year the whole app wears the day rather than just her
 * corner of it. Everything else — every other holiday, every ordinary
 * Wednesday — stays the lime instrument.
 */
export type BigDayKey = 'velines' | 'kucios' | 'kaledos' | 'uzgavenes' | 'metine' | 'gimtadienis'

export interface BigDayInputs {
  /** When this account first met her — the ISO timestamp in Dexie meta. */
  metAt?: string | null
  /** The resident's own birthday, `YYYY-MM-DD`, from the profile. */
  birthDate?: string | null
}

/**
 * At most one big day, highest first. Her own day outranks a household one:
 * on Vėlinės she is not sharing the billing.
 */
export function bigDayFor(date: Date, inputs: BigDayInputs = {}): BigDayKey | null {
  const month = date.getMonth() + 1
  const day = date.getDate()

  if (month === 11 && day === 2) return 'velines'
  if (month === 12 && day === 24) return 'kucios'
  if (month === 12 && day === 25) return 'kaledos'

  const uzgavenes = uzgavenesDate(date.getFullYear())
  if (uzgavenes.month === month && uzgavenes.day === day) return 'uzgavenes'

  if (isMeetingAnniversary(date, inputs.metAt)) return 'metine'
  if (isBirthday(date, inputs.birthDate)) return 'gimtadienis'

  return null
}

/**
 * The anniversary of the first meeting, from the first year on. Unlike the
 * account's own creation date this one is hers as much as yours, which is why
 * it gets a day and the account birthday does not.
 */
export function isMeetingAnniversary(date: Date, metAt: string | null | undefined): boolean {
  if (!metAt) return false
  const met = new Date(metAt)
  if (Number.isNaN(met.getTime())) return false
  return (
    date.getMonth() === met.getMonth() &&
    date.getDate() === met.getDate() &&
    date.getFullYear() > met.getFullYear()
  )
}

/** How many years this anniversary is, for a line that wants to say it. */
export function anniversaryYears(date: Date, metAt: string | null | undefined): number {
  if (!metAt) return 0
  const met = new Date(metAt)
  if (Number.isNaN(met.getTime())) return 0
  return date.getFullYear() - met.getFullYear()
}
